// Soft cap on the executive-summary length used to gate avatar-video generation.
// ~450 visible characters of TTS speech ≈ 30s, the practical limit for a video.
// This budget covers the ENTIRE spoken text — the typed summary PLUS the
// audio-only call-to-action below — so the two stay in sync at 450.
// It is NOT enforced on save — only surfaced as a counter and a pre-generation guard.
export const EXCERPT_MAX_CHARS = 450;

// Call-to-action the backend speaks at the end of every excerpt clip (see
// justidt-backend lib/tts.js EXCERPT_CTA_TEXT). It is audio-only — never stored
// in or displayed from the excerpt — so the counter reserves its spoken length
// here to keep summary + CTA within the budget. Keep identical to the backend.
const EXCERPT_CTA_TEXT = "Read more on J the App.";

/**
 * Visible-character count of rich-text HTML — i.e. roughly what the TTS engine
 * will actually speak (tags stripped, entities decoded, block/line breaks
 * counted as a single space). Used for the executive-summary counter and the
 * pre-generation length guard so the two always agree.
 */
export const getExcerptLength = (html: string): number => {
  if (!html) return 0;
  const withBreaks = html.replace(/<\/(p|div|li|h[1-6])>|<br\s*\/?>/gi, " ");
  const el = document.createElement("div");
  el.innerHTML = withBreaks;
  return (el.textContent || "").replace(/\s+/g, " ").trim().length;
};

const EXCERPT_CTA_LENGTH = EXCERPT_CTA_TEXT.length;

/**
 * Visible-character count of the full text that will actually be spoken: the
 * typed summary plus the audio-only CTA the backend appends at synthesis time.
 * The CTA is not stored, so its length is reserved here. Empty summary stays 0
 * (no audio is generated, so no CTA is spoken).
 */
export const getExcerptLengthWithCta = (html: string): number => {
  const summaryLength = getExcerptLength(html);
  return summaryLength === 0 ? 0 : summaryLength + EXCERPT_CTA_LENGTH;
};
